import { FC, useEffect, useState } from "react";
import "./EntitiesGenerationShimmer.scss";

const TOTAL_SECONDS = 60;

// Staged copy shown while the backend queue worker generates concepts, attributes, and
// relationships from the initiative's competency questions (mirrors CqGenerationShimmer's
// stage-progression pattern).
const STAGES = [
  { label: "Analyzing competency questions…", icon: "🔍", duration: 10 },
  { label: "Identifying concepts & entities…", icon: "🧩", duration: 12 },
  { label: "Mapping attributes…", icon: "🏷️", duration: 12 },
  { label: "Building relationships…", icon: "🔗", duration: 14 },
  { label: "Finalizing ontology structure…", icon: "✨", duration: 12 }
];

const EntitiesGenerationShimmer: FC = () => {
  const [activeStage, setActiveStage] = useState(0);
  const [secondsElapsed, setSecondsElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsElapsed((prev) =>
        prev < TOTAL_SECONDS ? prev + 1 : TOTAL_SECONDS
      );
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cumulative = 0;
    for (let i = 0; i < STAGES.length; i++) {
      cumulative += STAGES[i].duration;
      if (secondsElapsed < cumulative) {
        setActiveStage(i);
        return;
      }
    }
    setActiveStage(STAGES.length - 1);
  }, [secondsElapsed]);

  const progressPercent = (secondsElapsed / TOTAL_SECONDS) * 100;

  return (
    <div className="entities-generation-shimmer">
      <div className="entities-generation-shimmer__card">
        <div className="entities-generation-shimmer__header">
          <div className="entities-generation-shimmer__header-left">
            <div className="entities-generation-shimmer__pulse-ring">
              <span className="entities-generation-shimmer__status-dot" />
            </div>
            <div>
              <h2 className="entities-generation-shimmer__heading">
                Generating Entities &amp; Relationships
              </h2>
              <p className="entities-generation-shimmer__subtitle">
                Analyzing your competency questions to generate concepts,
                attributes, and relationships.
              </p>
            </div>
          </div>
        </div>

        <div className="entities-generation-shimmer__progress">
          <div
            className="entities-generation-shimmer__progress-fill"
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>

        <div className="entities-generation-shimmer__steps">
          {STAGES.map((stage, i) => {
            const isDone = i < activeStage;
            const isActive = i === activeStage;
            return (
              <div
                key={i}
                className={`entities-generation-shimmer__step ${
                  isDone
                    ? "entities-generation-shimmer__step--done"
                    : isActive
                    ? "entities-generation-shimmer__step--active"
                    : ""
                }`}
              >
                <div className="entities-generation-shimmer__step-icon">
                  {isDone ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <defs>
                        <linearGradient
                          id="entitiesGenerationCheckGrad"
                          x1="0"
                          y1="0"
                          x2="20"
                          y2="20"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0%" stopColor="#4F7BF7" />
                          <stop offset="100%" stopColor="#E84FAD" />
                        </linearGradient>
                      </defs>
                      <circle
                        cx="10"
                        cy="10"
                        r="10"
                        fill="url(#entitiesGenerationCheckGrad)"
                      />
                      <path
                        d="M6 10L9 13L14 7"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <span className="entities-generation-shimmer__step-emoji">
                      {stage.icon}
                    </span>
                  )}
                </div>
                <div className="entities-generation-shimmer__step-content">
                  <span className="entities-generation-shimmer__step-label">
                    {stage.label}
                  </span>
                  {isActive && (
                    <div className="entities-generation-shimmer__step-bar">
                      <div
                        className="entities-generation-shimmer__step-bar-fill"
                        style={{ animationDuration: `${stage.duration}s` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EntitiesGenerationShimmer;
